import { useEffect, useState } from 'react';
import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import type { Item } from '@dcc/rules/content/types.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import type { RoomInventory, SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import { usePlaySheet } from './usePlaySheet.ts';
import { clampQuantity, freeCopies } from './roomInventory.ts';
import { summarizeItem, type ItemSummaryLookup } from './itemSummary.ts';

const lookup: ItemSummaryLookup = {
  skillName: (id) => contentRegistry.getSkill(id)?.name,
  attack: (id) => contentRegistry.getAttack(id),
};

function ItemDetails({ item, itemId }: { item: Item | undefined; itemId: string }) {
  if (!item) {
    return <p className="dcc-creator-help">This sheet has no description for {itemId}. Ask your GM what it is.</p>;
  }
  const summary = summarizeItem(item, lookup);
  return (
    <>
      <p className="dcc-item-meta">{summary.kindLine}</p>
      {summary.effects.length > 0 && <p className="dcc-party-effects">{summary.effects.join(' · ')}</p>}
      {item.description && <p className="dcc-creator-help">{item.description}</p>}
      {item.notes && <p className="dcc-creator-help"><strong>Special rules:</strong> {item.notes}</p>}
    </>
  );
}

/**
 * The shared Party Inventory, and handing this crawler's Items to it.
 *
 * NOTHING HERE CHANGES AN INVENTORY ITSELF. Claim and Give ask the sheet server
 * to move the item; the server takes it from one owner and gives it to the
 * other in one transaction, so two players clicking at once cannot both get the
 * same item. This tab then re-reads what the server decided.
 */
export function PlayPartyTab(props: {
  services: AppServices;
  storage: SheetsApiStorage;
  characterId: string;
  inventory: RoomInventory | null;
  /** Bumped whenever the crawler record or the catalogue was re-read from the server. */
  epoch: number;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  /** The server moved something: reload this player's records and adopt the new stash. */
  onMoved: (inventory: RoomInventory) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const play = usePlaySheet(props.services, props.characterId, props.onSaveError, props.coordinationGate);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  // This tab never saves from its own copy of the crawler, so a stale copy can
  // only mislead the eye. Re-read whenever the server's version was re-read.
  useEffect(() => { play.reload(); }, [props.epoch]);

  if (play.error && !play.records) return <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>;
  if (!play.records) return <p>Loading…</p>;

  const { character } = play.records;
  const room = props.inventory;
  const closed = room?.combatActive === true;
  const disabled = busy || play.busy || closed;
  const amountFor = (key: string, max: number) => clampQuantity(amounts[key] ?? '1', max);

  async function move(kind: 'claim' | 'give', itemId: string, quantity: number, name: string) {
    if (busy) return;
    // The same lock every play action takes: a Dice+ roll in flight was
    // computed from the current crawler record and must save before it changes.
    const blocked = props.coordinationGate.claim();
    if (blocked) { setError(blocked.message); return; }
    setBusy(true);
    setError(null);
    setNote(null);
    let refreshAfterFailure = false;
    try {
      const result = kind === 'claim'
        ? await props.storage.claimItem(props.characterId, itemId, quantity)
        : await props.storage.depositItem(props.characterId, itemId, quantity);
      await props.onMoved(result.inventory);
      setNote(kind === 'claim'
        ? `${character.name} took ${quantity} × ${result.itemName} from the party.`
        : `${character.name} gave ${quantity} × ${name} to the party.`);
      setAmounts({});
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That item could not be moved.');
      // Whatever went wrong, show what the server actually holds now.
      refreshAfterFailure = true;
    } finally {
      props.coordinationGate.release();
      if (refreshAfterFailure) {
        try { await props.onRefresh(); } catch { /* the next poll will catch up */ }
      }
      setBusy(false);
      play.reload();
    }
  }

  const giveable = character.inventory
    .map((entry) => ({ entry, free: freeCopies(character, entry), item: contentRegistry.getItem(entry.itemId) }))
    .filter((row) => row.entry.quantity > 0);

  return (
    <div className="dcc-play">
      <section className="dcc-creator-card">
        <div className="dcc-item-title">
          <h2>Party Inventory</h2>
          <button className="dcc-creator-secondary" disabled={busy} onClick={() => void props.onRefresh()}>Refresh</button>
        </div>
        <p className="dcc-creator-help">Shared by the whole party. Taking something moves it to {character.name}; it is no longer here for anyone else.</p>
        {closed && <p className="dcc-creator-warn">The Party Inventory is closed during combat. Your GM can still hand items out.</p>}
        {room === null ? (
          <p className="dcc-empty">The Party Inventory could not be loaded. Press Refresh.</p>
        ) : room.party.length === 0 ? (
          <p className="dcc-empty">The party is carrying nothing in common.</p>
        ) : (
          <ul className="dcc-item-list">
            {room.party.map((row) => {
              const key = `claim:${row.itemId}`;
              const quantity = amountFor(key, row.quantity);
              return (
                <li key={row.itemId} className="dcc-item-row">
                  <div className="dcc-item-title"><strong>{row.name}</strong><span>× {row.quantity}</span></div>
                  <ItemDetails item={contentRegistry.getItem(row.itemId)} itemId={row.itemId} />
                  <div className="dcc-item-actions">
                    {row.quantity > 1 && (
                      <input
                        className="dcc-party-quantity" type="number" min={1} max={row.quantity} aria-label={`How many ${row.name} to take`}
                        value={amounts[key] ?? '1'} disabled={disabled}
                        onChange={(event) => setAmounts((current) => ({ ...current, [key]: event.target.value }))}
                      />
                    )}
                    <button disabled={disabled} onClick={() => void move('claim', row.itemId, quantity, row.name)}>
                      {row.quantity > 1 ? `Take ${quantity}` : 'Take'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="dcc-creator-card">
        <h2>Give to the party</h2>
        <p className="dcc-creator-help">Only copies that are not worn, held, on your Hotlist or with a companion can be given. Put those away first.</p>
        {giveable.length === 0 ? (
          <p className="dcc-empty">{character.name} is carrying nothing.</p>
        ) : (
          <ul className="dcc-item-list">
            {giveable.map(({ entry, free, item }) => {
              const key = `give:${entry.itemId}`;
              const name = item?.name ?? entry.itemId;
              const quantity = amountFor(key, free);
              return (
                <li key={entry.itemId} className="dcc-item-row">
                  <div className="dcc-item-title"><strong>{name}</strong><span>× {entry.quantity}</span></div>
                  <p className="dcc-item-meta">{free === entry.quantity ? 'All free to give' : `${free} free to give · ${entry.quantity - free} in use`}</p>
                  <div className="dcc-item-actions">
                    {free > 1 && (
                      <input
                        className="dcc-party-quantity" type="number" min={1} max={free} aria-label={`How many ${name} to give`}
                        value={amounts[key] ?? '1'} disabled={disabled}
                        onChange={(event) => setAmounts((current) => ({ ...current, [key]: event.target.value }))}
                      />
                    )}
                    <button className="dcc-creator-secondary" disabled={disabled || free < 1} onClick={() => void move('give', entry.itemId, quantity, name)}>
                      {free > 1 ? `Give ${quantity}` : 'Give'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {error && <p className="dcc-save-status dcc-save-warn" role="alert">{error}</p>}
      {note && <p className="dcc-save-status" role="status">{note}</p>}
    </div>
  );
}
